// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  type PartAttributeDefinitionVariantId,
  type PartCatalog,
  type PartListPart,
  useEvolveGet,
  useEvolvePaginatedGet,
} from '@evolvemep/api-ts';

import { useRouteSearchParam } from 'app/useRouteSearchParam';
import { isNotNil } from 'helpers/isNotNil';
import type { NeedsRefreshItem } from 'modules/Shop/CatalogSetup/CatalogIntrospector';
import { useCategoryPath, type CategoryPath } from 'modules/Shop/CatalogSetup/useCategoryPath';

type UseCatalogIntrospectorArgs = {
  partCatalog: PartCatalog;
  onPartSelected?: (part: PartListPart | null) => void;
  needsRefresh?: NeedsRefreshItem[];
  useQueryParams?: boolean;
  defaultCategory?: CategoryPath[number];
  /** Only `CatalogIntrospector` (desktop) has an attribute-filter pane; omit for callers without one. */
  partFilter?: PartAttributeDefinitionVariantId[];
};

/** Shared category/part browsing state and data-fetching behind both `CatalogIntrospector` and `MobileCatalogIntrospector`. */
export const useCatalogIntrospector = ({
  partCatalog,
  onPartSelected: onPartSelectedParent,
  needsRefresh,
  useQueryParams,
  defaultCategory,
  partFilter,
}: UseCatalogIntrospectorArgs) => {
  const [categoryPath, setCategoryPath, loadingCategoryPath] = useCategoryPath(defaultCategory);
  const selectedCategory = useMemo(() => categoryPath[categoryPath.length - 1], [categoryPath]);
  const [selectedPart, setSelectedPart] = useState<PartListPart | null>(null);

  const { apiCall: getCategory, loading: loadingCategory } = useEvolveGet('shop/partCatalog/category/:partCategoryId');

  const defaultOptsParts = useMemo(
    () => ({
      defaultConfig: {
        params: {
          partCategoryId: selectedCategory?.partCategoryId,
          partAttributeDefinitionVariantIds: partFilter,
        },
      },
    }),
    [partFilter, selectedCategory?.partCategoryId],
  );
  const {
    data: parts,
    fetchNextPage: fetchNextPageParts,
    setDefaultOpts: setDefaultOptsParts,
    loading: loadingParts,
    reset: resetParts,
  } = useEvolvePaginatedGet('shop/partCatalog/part', defaultOptsParts);
  useEffect(() => setDefaultOptsParts(defaultOptsParts), [defaultOptsParts, setDefaultOptsParts]);

  useEffect(() => {
    if (needsRefresh?.includes('parts')) resetParts();
  }, [needsRefresh, resetParts]);

  const [categoryIdFromParams, setCategoryIdFromParams] = useRouteSearchParam('/catalog-setup', 'categoryId');

  const onPartSelected = useCallback(
    async (part: PartListPart | null, navigateToCategory = true) => {
      if (isNotNil(part) && navigateToCategory && part.partCategoryId !== selectedCategory?.partCategoryId) {
        await getCategory({ url: `shop/partCatalog/category/${part.partCategoryId}` }).then((category) => {
          setCategoryPath(category);
          if (useQueryParams) setCategoryIdFromParams(category.partCategoryId);
        });
      }
      setTimeout(() => {
        setSelectedPart(part);
        onPartSelectedParent?.(part);
      });
    },
    [
      getCategory,
      onPartSelectedParent,
      selectedCategory?.partCategoryId,
      setCategoryIdFromParams,
      setCategoryPath,
      useQueryParams,
    ],
  );

  const selectedCatalogRef = useRef(partCatalog);
  useEffect(() => {
    if (partCatalog !== selectedCatalogRef.current) {
      selectedCatalogRef.current = partCatalog;
      setCategoryPath(null);
      void onPartSelected(null);
    }
  }, [setCategoryPath, onPartSelected, partCatalog]);

  const { apiCall: getPartCategoryApiCall, loading: loadingCategoryFromPath } = useEvolveGet(
    `shop/partCatalog/category/:partCategoryId`,
  );

  useEffect(() => {
    if (!useQueryParams) return;

    (async () => {
      if (categoryIdFromParams && categoryIdFromParams !== categoryPath[categoryPath.length - 1]?.partCategoryId) {
        await getPartCategoryApiCall({ url: `shop/partCatalog/category/${categoryIdFromParams}` })
          .then((category) => {
            if (category.partCatalogId === partCatalog.partCatalogId) {
              setCategoryPath(category);
            } else {
              throw new Error('Category does not belong to this catalog.');
            }
          })
          .catch((e) => {
            // oxlint-disable-next-line no-console
            console.warn('Failed to load category from params.', e);
          });
      }
    })();
  }, [
    categoryPath,
    getPartCategoryApiCall,
    setCategoryPath,
    partCatalog.partCatalogId,
    categoryIdFromParams,
    useQueryParams,
  ]);

  /** Navigates to a category: updates the path, clears the selected part, and (with `useQueryParams`) syncs the `categoryId` URL param. */
  const onCategorySelected = useCallback(
    (category: CategoryPath[number] | null) => {
      setCategoryPath(category);
      void onPartSelected(null);
      if (useQueryParams && category) {
        setCategoryIdFromParams(category.partCategoryId);
      }
    },
    [onPartSelected, setCategoryIdFromParams, setCategoryPath, useQueryParams],
  );

  return {
    categoryPath,
    selectedCategory,
    selectedPart,
    loadingCategoryPath,
    loadingCategory,
    loadingCategoryFromPath,
    parts,
    fetchNextPageParts,
    loadingParts,
    onCategorySelected,
    onPartSelected,
  };
};
